import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { AccentPromptChat } from '../ai/AccentPromptChat';
import { AccentPromptHistory } from '../ai/AccentPromptHistory';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { AccentTextAreaUnbound } from '../unbound/AccentTextAreaUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { accentUtils, t } from '../../services/HelperService';
import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { loadSharedTestOutputs } from './testing/nodeTestStateStorage';
import { testWorkflowExpression, testWorkflowSetVariable } from './workflowApi';
import { AiArtifactVersionChip } from '../ai/AiArtifactVersionChip';
import { sendJavascriptAssistantChatTurn } from '../ai/assistantChatApi';
import {
  extractHandlebarsTokens,
  collectCatalogTokens,
  useAiChatAssistant,
} from '../ai/useAiChatAssistant';

// Chat turns ride the async start/poll transport (see assistantChatApi.js): a long
// turn must never span one HTTP request, or the Azure front-end's 230s timeout
// kills it. This is the START query; the result is fetched by the shared transport.
const q_ChatStart = queryNoTracking('Workflow_Javascript_ChatStart', 0, true);
const q_TryFix = queryNoTracking('Workflow_Javascript_TryFix', 0, true);
const q_Explain = queryNoTracking('Workflow_Javascript_ExplainError', 0, true);
const q_Describe = queryNoTracking('Workflow_Javascript_Describe', 0, true);

const STATUS_IDLE = 'idle';
const STATUS_GENERATING = 'generating';
const STATUS_TESTING = 'testing';
const STATUS_FIXING = 'fixing';
const STATUS_EXPLAINING = 'explaining';
const STATUS_DESCRIBING = 'describing';

const safeString = (v) => (v ?? '').toString();
const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const stringifyValue = (value) => {
  if (value == null) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return `${value}`;
  }
};
const parseValueJson = (valueJson) => {
  const raw = safeString(valueJson);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};
const looksLikeHtml = (value) => (
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value)
);
const isSetVariableLike = (scriptKind) => ['setVariable', 'triggerOutput'].includes(safeString(scriptKind).trim());

const statusStyle = {
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const StatusBanner = ({ status, testOk, fixApplied, fixUnchanged, errorText, explanation, onExplainClick }) => {
  if (status === STATUS_GENERATING) return <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Thinking...')}</div>;
  if (status === STATUS_TESTING) return <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Testing expression...')}</div>;
  if (status === STATUS_FIXING) return <div style={{ ...statusStyle, background: '#fff3e0', color: '#e65100' }}><AccentSpinner size="sm" /> {t('Attempting to fix expression...')}</div>;
  if (status === STATUS_DESCRIBING) return <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Describing expression...')}</div>;
  if (fixApplied && testOk) return <div style={{ ...statusStyle, background: '#e8f5e9', color: '#2e7d32' }}>{t('Fix applied and workflow test passed.')}</div>;
  if (fixApplied) return <div style={{ ...statusStyle, background: '#fff3e0', color: '#e65100' }}>{t('Fix applied, but the workflow test still failed. Review the error below.')}</div>;
  if (fixUnchanged) return <div style={{ ...statusStyle, background: '#fff3e0', color: '#e65100' }}>{t('Fix attempted, but no changes could be made.')}</div>;
  if (testOk) return <div style={{ ...statusStyle, background: '#e8f5e9', color: '#2e7d32' }}>{t('Workflow test passed.')}</div>;
  if (errorText) {
    return (
      <div>
        <div style={{ ...statusStyle, background: '#fce4ec', color: '#b71c1c', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{errorText}</span>
          {!!onExplainClick && (
            <button
              type="button"
              onClick={onExplainClick}
              style={{ border: '1px solid #b71c1c', background: 'transparent', color: '#b71c1c', borderRadius: 4, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('What does this mean?')}
            </button>
          )}
        </div>
        {!!explanation && (
          <div style={{ padding: '8px 12px', background: '#fff8e1', borderRadius: '0 0 6px 6px', fontSize: 12, lineHeight: 1.5, color: '#5d4037', borderTop: '1px solid #ffe0b2' }}>
            <strong>{t('Explanation:')}</strong> {explanation}
          </div>
        )}
      </div>
    );
  }
  return null;
};

export const AccentWorkflowJavascriptAssistantDlg = ({
  onClose,
  existingScript,
  scriptKind = 'expression',
  variableName,
  workflowDefinitionID,
  templateCatalog,
  scriptTemplateCatalog,
  promptTemplateCatalog,
  runtimeNotes,
  label,
  initialVariablesJson = '{}',
  initialTemplateValues,
  // The *_ChatStart query for this host's assistant kind (custom validation hosts
  // pass CustomValidation_Javascript_ChatStart). Must be a START endpoint: the
  // transport polls Assistant_ChatResult for the outcome.
  chatStartQuery = q_ChatStart,
  tryFixQuery = q_TryFix,
  explainQuery = q_Explain,
  describeQuery = q_Describe,
  executeTest,
  testButtonLabel,
  requestContext,
}) => {
  const [scriptEditorKey, setScriptEditorKey] = React.useState(0);
  const [scriptText, setScriptText] = React.useState(() => safeString(existingScript));
  const [variablesJson, setVariablesJson] = React.useState(() => safeString(initialVariablesJson) || '{}');
  const [templateValues, setTemplateValues] = React.useState(() => ({ ...(initialTemplateValues ?? {}) }));
  const [lastResult, setLastResult] = React.useState(null);
  const [status, setStatus] = React.useState(STATUS_IDLE);
  const [fixApplied, setFixApplied] = React.useState(false);
  const [fixUnchanged, setFixUnchanged] = React.useState(false);
  const [variablesJsonError, setVariablesJsonError] = React.useState('');
  const [explanation, setExplanation] = React.useState('');
  const [scriptDescription, setScriptDescription] = React.useState('');
  const [lastPromptTokens, setLastPromptTokens] = React.useState([]);

  const {
    chatMessages,
    setChatMessages,
    pushMsg,
    pushVersion,
    getVersion,
    buildServerMessages,
  } = useAiChatAssistant({ initialArtifact: existingScript });

  // One id per dialog open: every chat turn sends it so the server routes the turn's
  // diagnostic capture under this session (correlation for bug reports / flow logs).
  const diagnosticSessionIdRef = React.useRef(`js_${makeId()}`);

  const busy = status !== STATUS_IDLE;
  const canOk = !accentUtils.isEmpty(scriptText);
  const executableTemplateCatalog = React.useMemo(() => {
    if (Array.isArray(scriptTemplateCatalog)) return scriptTemplateCatalog;
    return Array.isArray(templateCatalog) ? templateCatalog : [];
  }, [scriptTemplateCatalog, templateCatalog]);
  const aiPromptTemplateCatalog = React.useMemo(() => {
    if (Array.isArray(promptTemplateCatalog)) return promptTemplateCatalog;
    return executableTemplateCatalog;
  }, [executableTemplateCatalog, promptTemplateCatalog]);
  const sharedOutputs = React.useMemo(() => loadSharedTestOutputs(workflowDefinitionID), [workflowDefinitionID]);
  const allowedTemplates = React.useMemo(() => collectCatalogTokens(executableTemplateCatalog), [executableTemplateCatalog]);
  const usedTemplates = React.useMemo(() => {
    return Array.from(new Set([
      ...extractHandlebarsTokens(scriptText),
      ...(Array.isArray(lastPromptTokens) ? lastPromptTokens : []),
    ])).filter(Boolean);
  }, [lastPromptTokens, scriptText]);
  const hydratedVariablesPreviewJson = safeString(requestContext?.hydratedVariablesPreviewJson);

  const normalizeTestResult = React.useCallback((res) => {
    if (isSetVariableLike(scriptKind)) {
      const success = !!(res?.testOk ?? res?.TestOk ?? res?.success ?? res?.Success);
      return {
        testOk: success,
        error: safeString(res?.error ?? res?.Error),
        variableName: safeString(res?.variableName ?? res?.VariableName ?? variableName),
        valueJson: safeString(res?.valueJson ?? res?.ValueJson),
        outputVariablesJson: safeString(res?.outputVariablesJson ?? res?.OutputVariablesJson),
      };
    }

    return {
      testOk: !!(res?.testOk ?? res?.TestOk ?? res?.isValidSyntax ?? res?.IsValidSyntax),
      error: safeString(res?.error ?? res?.Error),
      normalizedExpression: safeString(res?.normalizedExpression ?? res?.NormalizedExpression),
      booleanResult: res?.booleanResult ?? res?.BooleanResult ?? res?.result ?? res?.Result,
      variablesJson: safeString(res?.variablesJson ?? res?.VariablesJson),
    };
  }, [scriptKind, variableName]);

  const validateVariablesJson = React.useCallback(() => {
    try {
      const raw = safeString(variablesJson).trim() || '{}';
      JSON.parse(raw);
      setVariablesJsonError('');
      return raw;
    } catch (err) {
      const message = safeString(err?.message) || t('Unable to parse variables JSON.');
      setVariablesJsonError(message);
      setLastResult(null);
      pushMsg('error', message);
      return null;
    }
  }, [pushMsg, variablesJson]);

  const runTestInternal = React.useCallback(async (script) => {
    const rawVars = validateVariablesJson();
    if (rawVars == null) return null;

    if (isSetVariableLike(scriptKind)) {
      return throwIfFrameworkException(await testWorkflowSetVariable({
        name: safeString(variableName) || 'result',
        valueMode: 'javascript',
        valueTemplate: '',
        script: safeString(script),
        variablesJson: rawVars,
        templateValues,
      }));
    }

      if (executeTest) {
        return throwIfFrameworkException(await executeTest({
          script: safeString(script),
          scriptKind: safeString(scriptKind),
          variableName: safeString(variableName),
          variablesJson: rawVars,
          templateValues,
        }));
      }

      return throwIfFrameworkException(await testWorkflowExpression({
      expression: safeString(script),
      variablesJson: rawVars,
      templateValues,
    }));
  }, [executeTest, scriptKind, templateValues, validateVariablesJson, variableName]);

  const clearFixFlags = React.useCallback(() => {
    setFixApplied(false);
    setFixUnchanged(false);
    setExplanation('');
  }, []);

  const onChatPrompt = React.useCallback(async ({ text, history, images }) => {
    try {
      clearFixFlags();
      const rawVars = validateVariablesJson();
      if (rawVars == null) return false;

      const tokensInPrompt = extractHandlebarsTokens(text);
      setLastPromptTokens(tokensInPrompt);

      // Record any manual edits as a version so restore points stay coherent.
      pushVersion(scriptText, 'manual');

      setStatus(STATUS_GENERATING);
      // Async start/poll transport (see assistantChatApi.js): handled 500s on the
      // start call and pipeline failures inside the turn both throw their message,
      // so the catch below shows them as an error turn.
      const response = await sendJavascriptAssistantChatTurn(chatStartQuery, {
        prompt: safeString(text),
        messages: buildServerMessages(history, text),
        currentScript: safeString(scriptText),
        lastError: safeString(lastResult?.Error ?? lastResult?.error),
        scriptKind: safeString(scriptKind),
        variableName: safeString(variableName),
        variablesJson: rawVars,
        templateValues,
        allowedTemplates,
        hydratedVariablesPreviewJson,
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        diagnosticSessionID: diagnosticSessionIdRef.current,
        // Screenshots pasted with this message (data URLs). The server shows them to
        // the authoring model as images; later turns carry them on the history message.
        promptImages: Array.isArray(images) ? images : [],
        ...(requestContext ?? {}),
      });

      const message = safeString(response?.Message ?? response?.message);
      const updated = !!(response?.ArtifactUpdated ?? response?.artifactUpdated);
      const nextScript = safeString(response?.Script ?? response?.script);

      if (!updated || accentUtils.isEmpty(nextScript)) {
        // Discussion-only turn: show the reply, leave the script and test state untouched.
        pushMsg('assistant', message || t('I could not produce a response. Please try rephrasing.'));
        return true;
      }

      const normalized = normalizeTestResult(response);
      const version = pushVersion(nextScript, 'ai');

      setScriptText(nextScript);
      setScriptDescription('');
      setScriptEditorKey(k => k + 1);
      setLastResult(response ?? null);

      pushMsg('assistant', message || t('I have updated the script.'), {
        version: version ? { id: version.id, seq: version.seq, testOk: normalized.testOk } : undefined,
      });

      if (normalized.testOk) {
        pushMsg('system', 'Workflow test passed.');
      } else if (normalized.error) {
        pushMsg('error', `Workflow test failed: ${normalized.error}`);
      }

      return true;
    } catch (err) {
      // Server errors may be localization keys (e.g. the AI usage limit); resolve them.
      const raw = safeString(err?.message) || safeString(err);
      const errorText = raw ? t(raw) : t('AI request failed.');
      setLastResult({ Error: errorText });
      pushMsg('error', errorText);
      return false;
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [allowedTemplates, buildServerMessages, chatStartQuery, clearFixFlags, hydratedVariablesPreviewJson, lastResult, normalizeTestResult, pushMsg, pushVersion, requestContext, runtimeNotes, scriptKind, scriptText, templateValues, validateVariablesJson, variableName]);

  const onRestoreVersion = React.useCallback((versionId) => {
    const version = getVersion(versionId);
    if (!version) return;
    setScriptText(version.content);
    setScriptDescription('');
    setScriptEditorKey(k => k + 1);
    clearFixFlags();
    setLastResult(null);
    pushMsg('system', `Restored script v${version.seq}.`);
  }, [clearFixFlags, getVersion, pushMsg]);

  const renderMessageExtra = React.useCallback((m) => {
    const version = m?.version;
    if (!version) return null;
    return (
      <AiArtifactVersionChip
        label={`${t('Script')} v${version.seq}`}
        ok={version.testOk}
        title={t('Restore this version into the editor')}
        onClick={() => onRestoreVersion(version.id)}
      />
    );
  }, [onRestoreVersion]);

  const onTestClick = async (e) => {
    try {
      clearFixFlags();
      setStatus(STATUS_TESTING);
      pushMsg('system', 'Testing expression...');
      const res = await runTestInternal(scriptText);
      if (!res) return;
      setLastResult(res ?? null);
      const normalized = normalizeTestResult(res);
      if (normalized.testOk) {
        pushMsg('system', 'Workflow test passed.');
      } else if (normalized.error) {
        pushMsg('error', `Workflow test failed: ${normalized.error}`);
      }
    } catch (err) {
      const raw = safeString(err?.message) || safeString(err);
      const errorText = raw ? t(raw) : t('Test failed.');
      setLastResult({ Error: errorText });
      pushMsg('error', errorText);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  const onTryFixClick = async (e) => {
    try {
      clearFixFlags();
      const rawVars = validateVariablesJson();
      if (rawVars == null) return;

      setStatus(STATUS_FIXING);
      pushMsg('system', 'Attempting auto-fix...');
      const res = throwIfFrameworkException(await tryFixQuery.getFirstOrDefault({
        script: safeString(scriptText),
        scriptKind: safeString(scriptKind),
        variableName: safeString(variableName),
        variablesJson: rawVars,
        templateValues,
        allowedTemplates,
        hydratedVariablesPreviewJson,
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        error: safeString(lastResult?.Error ?? lastResult?.error),
        ...(requestContext ?? {}),
      }));

      setLastResult(res ?? null);
      const nextScript = safeString(res?.Script ?? res?.script);
      const changed = !accentUtils.isEmpty(nextScript) && nextScript !== scriptText;
      if (changed) {
        const version = pushVersion(nextScript, 'fix');
        setScriptText(nextScript);
        setScriptDescription('');
        setScriptEditorKey(k => k + 1);
        setFixApplied(true);
        pushMsg('assistant', 'Fix applied — the expression has been updated.', {
          version: version ? { id: version.id, seq: version.seq, testOk: !!(res?.TestOk ?? res?.testOk) } : undefined,
        });
      } else {
        setFixUnchanged(true);
      }

      const fixNotes = res?.FixNotes ?? res?.fixNotes;
      if (Array.isArray(fixNotes) && fixNotes.length > 0) {
        pushMsg('system', fixNotes.join(' '));
      }

      const normalized = normalizeTestResult(res);
      if (normalized.testOk) {
        pushMsg('system', 'Workflow test passed after fix.');
      } else if (normalized.error) {
        pushMsg('error', `Workflow test failed: ${normalized.error}`);
      }
    } catch (err) {
      const raw = safeString(err?.message) || safeString(err);
      const errorText = raw ? t(raw) : t('Fix failed.');
      setLastResult({ Error: errorText });
      pushMsg('error', errorText);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  const onExplainClick = React.useCallback(async () => {
    const rawError = safeString(lastResult?.Error ?? lastResult?.error);
    if (!rawError) return;
    try {
      setStatus(STATUS_EXPLAINING);
      pushMsg('system', 'Asking AI to explain this error...');
      const res = throwIfFrameworkException(await explainQuery.getFirstOrDefault({
        script: safeString(scriptText),
        scriptKind: safeString(scriptKind),
        error: rawError,
        hydratedVariablesPreviewJson,
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        ...(requestContext ?? {}),
      }));
      const explainText = safeString(res?.Explanation ?? res?.explanation) || t('Could not generate an explanation.');
      setExplanation(explainText);
      pushMsg('assistant', `Explanation: ${explainText}`);
    } catch (err) {
      const raw = safeString(err?.message) || safeString(err);
      const errorText = raw ? t(raw) : t('Failed to explain error.');
      setExplanation(errorText);
      pushMsg('error', errorText);
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [explainQuery, hydratedVariablesPreviewJson, lastResult, pushMsg, requestContext, runtimeNotes, scriptKind, scriptText]);

  const onDescribeClick = React.useCallback(async (e) => {
    try {
      setStatus(STATUS_DESCRIBING);
      pushMsg('system', 'Asking AI to describe this expression...');
      const res = throwIfFrameworkException(await describeQuery.getFirstOrDefault({
        script: safeString(scriptText),
        scriptKind: safeString(scriptKind),
        variableName: safeString(variableName),
        variablesJson: safeString(variablesJson).trim() || '{}',
        templateValues,
        allowedTemplates,
        hydratedVariablesPreviewJson,
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        ...(requestContext ?? {}),
      }));
      const describeText = safeString(res?.Description ?? res?.description) || t('Could not generate a description.');
      setScriptDescription(describeText);
      pushMsg('assistant', `Expression description: ${describeText}`);
    } catch (err) {
      const raw = safeString(err?.message) || safeString(err);
      const errorText = raw ? t(raw) : t('Failed to describe expression.');
      setScriptDescription(errorText);
      pushMsg('error', errorText);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  }, [allowedTemplates, describeQuery, hydratedVariablesPreviewJson, pushMsg, requestContext, runtimeNotes, scriptKind, scriptText, templateValues, variableName, variablesJson]);

  const doOk = (e) => {
    onClose?.({ canceled: false, script: safeString(scriptText) });
    e?.promise?.();
  };

  const normalizedResult = normalizeTestResult(lastResult);
  const evaluatedPreviewValue = parseValueJson(normalizedResult.valueJson);
  const evaluatedValueIsHtml = looksLikeHtml(evaluatedPreviewValue);

  return (
    <AccentDialog
      headerContent={label ? `${label} ${t('AI Assistant')}` : t('Workflow JavaScript AI Assistant')}
      onClose={onClose}
      relativeHeight={(d) => d.height - 120}
      relativeWidth={(d) => d.width - 120}
      actionsContent={[
        <AccentButton key="use" tagName="ok" disabled={busy || !canOk} onClick={doOk}>
          {isSetVariableLike(scriptKind) ? t('Use this script') : t('Use this expression')}
        </AccentButton>,
      ]}
    >
      <div style={{ display: 'flex', gap: 16, height: '100%', minHeight: 0, background: '#fff' }}>
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          <StatusBanner
            status={status}
            testOk={normalizedResult.testOk}
            fixApplied={fixApplied}
            fixUnchanged={fixUnchanged}
            errorText={normalizedResult.error}
            explanation={explanation}
            onExplainClick={onExplainClick}
          />

          {Array.isArray(runtimeNotes) && runtimeNotes.length > 0 && (
            <div style={{ border: '1px solid #e5e5e5', borderRadius: 6, padding: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('Runtime notes')}</div>
              <div style={{ fontSize: 12, opacity: 0.8, whiteSpace: 'pre-wrap' }}>
                {runtimeNotes.join('\n')}
              </div>
            </div>
          )}

          {!!hydratedVariablesPreviewJson && hydratedVariablesPreviewJson !== '{}' && (
            <details style={{ border: '1px solid #e5e5e5', borderRadius: 6, padding: 10 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>{t('Hydrated ctx.vars preview')}</summary>
              <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>
                {hydratedVariablesPreviewJson}
              </pre>
            </details>
          )}

          {sharedOutputs.length > 0 && (
            <div style={{ border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, display: 'grid', gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Load upstream output')}</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>{t('Use saved downstream test context, including BatchTimer Run Now output, as the current variables JSON.')}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {sharedOutputs.map((entry) => (
                  <button
                    key={entry.nodeId}
                    type="button"
                    onClick={() => {
                      try {
                        setVariablesJson(JSON.stringify(entry.variables ?? {}, null, 2));
                        setVariablesJsonError('');
                      } catch { /* ignore */ }
                    }}
                    style={{ border: '1px solid #adb5bd', background: '#f8f9fa', cursor: 'pointer', padding: '2px 10px', borderRadius: 4, fontSize: 12, color: '#495057' }}
                    title={`${entry.nodeLabel || entry.nodeId} (${entry.savedAt || ''})`}
                  >
                    {entry.nodeLabel || entry.nodeId}
                  </button>
                ))}
              </div>
            </div>
          )}

          {usedTemplates.length > 0 && (
            <div style={{ border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, display: 'grid', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Template values')}</div>
              {usedTemplates.map((token) => (
                <AccentTextUnbound
                  key={token}
                  label={token}
                  disabled={busy}
                  defaultValue={safeString(templateValues?.[token])}
                  onChange={(value) => setTemplateValues(prev => ({ ...(prev ?? {}), [token]: safeString(value) }))}
                  maxLength={4000}
                />
              ))}
            </div>
          )}

          <AccentTemplateTextAreaUnbound
            key={scriptEditorKey}
            label={isSetVariableLike(scriptKind) ? t('Current JavaScript script') : t('Current JavaScript expression')}
            rows={8}
            max={12000}
            defaultValue={scriptText}
            onChange={(value) => { setScriptText(value); setScriptDescription(''); }}
            templateCatalog={executableTemplateCatalog}
            insertMode="snippet"
            style={{ width: '100%' }}
          />

          <AccentTextAreaUnbound
            label={t('Test variables (JSON object)')}
            rows={10}
            max={16000}
            defaultValue={variablesJson}
            onChange={setVariablesJson}
            style={{ width: '100%' }}
          />
          {!!variablesJsonError && (
            <div className="alert alert-danger" style={{ marginBottom: 0 }}>
              {variablesJsonError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <AccentButton onClick={onTestClick} disabled={busy || accentUtils.isEmpty(scriptText)}>
              {status === STATUS_TESTING ? t('Testing...') : (testButtonLabel ?? t('Run workflow test'))}
            </AccentButton>
            <AccentButton outline onClick={onTryFixClick} disabled={busy || accentUtils.isEmpty(scriptText)}>
              {status === STATUS_FIXING ? t('Fixing...') : t('Try fix')}
            </AccentButton>
            <AccentButton outline size="sm" tagName="describeExpression" onClick={onDescribeClick} disabled={busy || accentUtils.isEmpty(scriptText)} title={t('Describe this expression')}>
              {t('Explain Expression')}
            </AccentButton>
          </div>

          {!!scriptDescription && status === STATUS_IDLE && (
            <div style={{ padding: '8px 12px', background: '#eef7ff', border: '1px solid #bbdefb', borderRadius: 6, fontSize: 12, lineHeight: 1.5, color: '#0d47a1' }}>
              <strong>{t('What this expression does:')}</strong> {scriptDescription}
            </div>
          )}

          {!!lastResult && (
            <div style={{ border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, display: 'grid', gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Workflow test result')}</div>
              {isSetVariableLike(scriptKind) ? (
                <>
                  <div><strong>{t('Success')}:</strong> {normalizedResult.testOk ? t('Yes') : t('No')}</div>
                  <div><strong>{t('Variable')}:</strong> {normalizedResult.variableName || safeString(variableName) || t('N/A')}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{t('Evaluated value')}</div>
                    {evaluatedValueIsHtml ? (
                      <>
                        <div
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            padding: 10,
                            background: '#fff',
                            minHeight: 42,
                          }}
                          dangerouslySetInnerHTML={{ __html: evaluatedPreviewValue || '' }}
                        />
                        <details style={{ marginTop: 6 }}>
                          <summary style={{ cursor: 'pointer' }}>{t('Raw value')}</summary>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6 }}>
                            {normalizedResult.valueJson || 'null'}
                          </div>
                        </details>
                      </>
                    ) : (
                      <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {normalizedResult.valueJson || 'null'}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{t('Output variables')}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {normalizedResult.outputVariablesJson || '{}'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div><strong>{t('Syntax valid')}:</strong> {normalizedResult.testOk ? t('Yes') : t('No')}</div>
                  <div><strong>{t('Result')}:</strong> {normalizedResult.booleanResult == null ? t('N/A') : `${normalizedResult.booleanResult}`}</div>
                  {!!normalizedResult.normalizedExpression && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{t('Normalized expression')}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {normalizedResult.normalizedExpression}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{t('Resolved variables')}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {normalizedResult.variablesJson || stringifyValue(lastResult?.Variables ?? lastResult?.variables)}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ flex: '0 0 40%', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 12, borderLeft: '1px solid #eee', paddingLeft: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Conversation')}</div>
          <div style={{ flex: '1 1 auto', minHeight: 140 }}>
            <AccentPromptHistory messages={chatMessages} fillHeight renderMessageExtra={renderMessageExtra} />
          </div>
          <AccentPromptChat
            messages={chatMessages}
            onMessagesChange={setChatMessages}
            onSendPrompt={onChatPrompt}
            disabled={busy}
            promptLabel={t('AI prompt')}
            templateCatalog={aiPromptTemplateCatalog}
            placeholder={t('Ask a question or describe a change...')}
          />
        </div>
      </div>
    </AccentDialog>
  );
};
